import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Tables } from '@/integrations/supabase/types';
import { useLanguage } from '@/contexts/LanguageContext';

type Group = Tables<'groups'>;

interface LeaderboardEntry {
  user_id: string;
  nickname: string;
  total: number;
  longestStreak: number;
  rank: number;
}

interface PastChallengeCardProps {
  group: Group;
  userStats: {
    totalDrinks: number;
    longestStreak: number;
    beerCount: number;
    wineCount: number;
    shotCount: number;
  };
  leaderboard: LeaderboardEntry[];
  userNickname: string;
}

export function PastChallengeCard({ group, userStats, leaderboard, userNickname }: PastChallengeCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, language } = useLanguage();

  // Determine status: "Ended early" if cancelled before challenge_end, otherwise "Completed"
  const now = new Date();
  const challengeEnd = new Date(group.challenge_end);
  const wasEndedEarly = group.status === 'completed' && challengeEnd > now;
  const statusLabel = wasEndedEarly ? t('pastChallengeEndedEarly') : t('pastChallengeCompleted');
  const statusVariant = wasEndedEarly ? 'secondary' : 'default';

  // Format date range
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'sk' ? 'sk-SK' : 'en-GB', { 
      day: 'numeric', 
      month: 'numeric', 
      year: 'numeric' 
    });
  };

  const dateRange = `${formatDate(group.challenge_start)} – ${formatDate(group.challenge_end)}`;

  // Find user's rank in leaderboard
  const userRank = leaderboard.find(e => e.nickname === userNickname)?.rank || '-';

  return (
    <Card className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full text-left">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <span className="truncate">{dateRange}</span>
                  <Badge variant={statusVariant} className="shrink-0 text-xs">
                    {statusLabel}
                  </Badge>
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {dateRange}
                </p>
              </div>
              <div className="shrink-0 text-muted-foreground">
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* Personal Stats */}
            <div>
              <h4 className="text-sm font-medium mb-2">{t('pastChallengeYourStats')}</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-muted/30 rounded text-center">
                  <div className="text-xl font-bold text-primary">{userStats.totalDrinks}</div>
                  <div className="text-xs text-muted-foreground">{t('pastChallengeTotalDrinks')}</div>
                </div>
                <div className="p-2 bg-muted/30 rounded text-center">
                  <div className="text-xl font-bold">{userStats.longestStreak}</div>
                  <div className="text-xs text-muted-foreground">{t('pastChallengeLongestStreak')}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center text-sm">
                <div>
                  <span className="text-lg">🍺</span> {userStats.beerCount}
                </div>
                <div>
                  <span className="text-lg">🍷</span> {userStats.wineCount}
                </div>
                <div>
                  <span className="text-lg">🥃</span> {userStats.shotCount}
                </div>
              </div>
            </div>

            {/* Final Leaderboard */}
            {leaderboard.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">{t('pastChallengeFinalLeaderboard')}</h4>
                <div className="space-y-1">
                  {/* Header */}
                  <div className="grid grid-cols-[24px_1fr_40px_40px] gap-2 text-xs text-muted-foreground font-medium pb-1 border-b border-border">
                    <span>#</span>
                    <span>{t('leaderboardUser')}</span>
                    <span className="text-center">Σ</span>
                    <span className="text-center">🗓️</span>
                  </div>
                  
                  {/* Rows */}
                  {leaderboard.map((entry) => {
                    const isCurrentUser = entry.nickname === userNickname;
                    return (
                      <div 
                        key={entry.user_id}
                        className={`grid grid-cols-[24px_1fr_40px_40px] gap-2 py-1.5 text-sm ${isCurrentUser ? 'bg-primary/10 -mx-2 px-2 rounded' : ''}`}
                      >
                        <span className="font-medium">{entry.rank}</span>
                        <span className={`truncate ${isCurrentUser ? 'font-semibold' : ''}`}>
                          {entry.nickname}
                        </span>
                        <span className="text-center">{entry.total}</span>
                        <span className="text-center">{entry.longestStreak}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
